/**
 * Content Accordion Component
 * Handles accordion expand/collapse behavior and accessibility
 */

class ContentAccordion {
  constructor(section) {
    this.section = section;
    this.autoClose = this.section.dataset.autoClose === 'true';
    this.accordionItems = this.section.querySelectorAll('.accordion-item');
    this.groupDetails = this.section.querySelectorAll('details.accordion-group');

    this.init();
  }

  init() {
    this.initGroupAccordions();

    this.accordionItems.forEach(item => {
      const summary = item.querySelector('.accordion-item__question');
      
      if (!summary) return;

      // Add ARIA attributes for accessibility
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
      
      // Handle click events
      summary.addEventListener('click', (e) => {
        // Update aria-expanded before toggle completes
        const willBeOpen = !item.open;
        summary.setAttribute('aria-expanded', willBeOpen ? 'true' : 'false');
        
        // Auto-close other items if enabled
        if (this.autoClose && willBeOpen) {
          this.closeAllExcept(item);
        }
      });

      // Handle keyboard navigation
      summary.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          summary.click();
        }
      });

      // Update aria-expanded on toggle
      item.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
      });
    });
  }

  initGroupAccordions() {
    this.groupDetails.forEach(detailsEl => {
      const summary = detailsEl.querySelector('.accordion-group__title');
      if (!summary) return;

      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', detailsEl.open ? 'true' : 'false');

      summary.addEventListener('click', () => {
        const willBeOpen = !detailsEl.open;
        summary.setAttribute('aria-expanded', willBeOpen ? 'true' : 'false');
      });

      summary.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          summary.click();
        }
      });

      detailsEl.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', detailsEl.open ? 'true' : 'false');
      });
    });
  }

  closeAllExcept(currentItem) {
    this.accordionItems.forEach(item => {
      if (item !== currentItem && item.open) {
        item.open = false;
        const summary = item.querySelector('.accordion-item__question');
        if (summary) {
          summary.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  destroy() {
    this.accordionItems.forEach(item => {
      const summary = item.querySelector('.accordion-item__question');
      if (summary) {
        const newSummary = summary.cloneNode(true);
        summary.parentNode.replaceChild(newSummary, summary);
      }
    });

    this.section.querySelectorAll('details.accordion-group .accordion-group__title').forEach(summary => {
      const newSummary = summary.cloneNode(true);
      summary.parentNode.replaceChild(newSummary, summary);
    });
  }
}

// Initialize all accordion sections on page load
function initContentAccordions() {
  const accordionSections = document.querySelectorAll('[data-section-type="content-accordion"]');
  
  accordionSections.forEach(section => {
    if (!section.accordionInstance) {
      section.accordionInstance = new ContentAccordion(section);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initContentAccordions);
} else {
  initContentAccordions();
}

// Shopify Theme Editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('[data-section-type="content-accordion"]');
    if (section) {
      section.accordionInstance = new ContentAccordion(section);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('[data-section-type="content-accordion"]');
    if (section && section.accordionInstance) {
      section.accordionInstance.destroy();
      section.accordionInstance = null;
    }
  });

  document.addEventListener('shopify:section:select', function(event) {
    const section = event.target.querySelector('[data-section-type="content-accordion"]');
    if (section) {
      // Scroll to section if needed
      section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  });

  document.addEventListener('shopify:block:select', function(event) {
    const block = event.target;

    if (block.matches && block.matches('details.accordion-group')) {
      block.open = true;
      const groupSummary = block.querySelector('.accordion-group__title');
      if (groupSummary) {
        groupSummary.setAttribute('aria-expanded', 'true');
      }
    }

    if (block.classList.contains('accordion-item')) {
      const parentGroup = block.closest('details.accordion-group');
      if (parentGroup) {
        parentGroup.open = true;
        const groupSummary = parentGroup.querySelector('.accordion-group__title');
        if (groupSummary) {
          groupSummary.setAttribute('aria-expanded', 'true');
        }
      }

      block.open = true;
      const summary = block.querySelector('.accordion-item__question');
      if (summary) {
        summary.setAttribute('aria-expanded', 'true');
      }
    }

    block.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
}

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ContentAccordion;
}
